import { Shield, Sparkles, Zap, ArrowRight } from "lucide-react";
import heroImage from "@/assets/hero-image-new.png";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { RotatingText } from "@/components/RotatingText";

export const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const heroRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Parallax effect for hero image
  useEffect(() => {
    if (prefersReducedMotion) return;

    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const scrollProgress = -rect.top / window.innerHeight;
        setParallaxY(scrollProgress * 30);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prefersReducedMotion]);

  const featurePills = [
    { icon: Sparkles, text: "Works inside Outlook", color: "text-brand-orange" },
    { icon: Zap, text: "Credit-based pricing", color: "text-brand-cyan" },
    { icon: Shield, text: "Privacy-First", color: "text-brand-pink" },
  ];

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero"
    >
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-64 md:w-96 h-64 md:h-96 bg-brand-pink/20 rounded-full blur-[120px] animate-blob" />
        <div className="absolute top-40 right-10 w-64 md:w-96 h-64 md:h-96 bg-brand-cyan/20 rounded-full blur-[120px] animate-blob animation-delay-2000" />
        <div className="absolute bottom-20 left-1/3 w-64 md:w-96 h-64 md:h-96 bg-brand-orange/20 rounded-full blur-[120px] animate-blob animation-delay-4000" />
      </div>
      
      {/* Radial Gradient Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-glow/30 via-transparent to-transparent" />
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      
      <div className="container relative z-10 px-4 py-12 md:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-6 md:space-y-8">
            {/* Badge with Animation */}
            <div 
              className={`inline-flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-full glass-card-strong shadow-lg transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <Sparkles className="w-4 h-4 text-brand-orange animate-pulse-glow" />
              <span className="text-xs md:text-sm font-semibold text-primary-foreground">The AI assistant for Outlook</span>
            </div>
            
            {/* Main Headline with Staggered Animation */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-primary-foreground leading-tight space-y-2">
              <span 
                className={`block transition-all duration-700 delay-100 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                From inbox chaos
              </span>
              <span 
                className={`block transition-all duration-700 delay-200 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                to contact{" "}
                <RotatingText 
                  words={["clarity.", "intelligence.", "power."]} 
                  interval={3000}
                  className="bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent animate-gradient-shift"
                />
              </span>
            </h1>
            
            {/* Subheadline */}
            <p 
              className={`text-lg sm:text-xl md:text-2xl text-primary-foreground/90 max-w-2xl leading-relaxed transition-all duration-700 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Automatically extracts names, companies, job titles, phone numbers, emails and websites from email signatures and saves complete contacts to Microsoft Outlook in one click.
            </p>
            
            <p 
              className={`text-base md:text-lg text-primary-foreground/75 max-w-2xl transition-all duration-700 delay-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              No new system to learn. Works where you already work — Outlook.
            </p>
            
            {/* CTA Buttons */}
            <div 
              className={`flex flex-col gap-6 transition-all duration-700 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md mx-auto lg:mx-0">
                <Button 
                  size="lg" 
                  asChild 
                  className="flex-1 bg-white text-primary hover:bg-white/90 shadow-elevated hover:shadow-glow transition-all duration-300 hover:-translate-y-1 group touch-target touch-active"
                >
                  <a href="https://marketplace.microsoft.com/en-us/product/WA200010021?tab=Overview" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                    Get Add-in
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button 
                  size="lg" 
                  variant="ghost" 
                  asChild 
                  className="flex-1 glass-card-strong text-primary-foreground hover:bg-white/20 border-white/30 hover:border-white/50 transition-all duration-300 touch-target touch-active"
                >
                  <a href="#how-it-works">See it in action</a>
                </Button>
              </div>
              
              {/* Feature Pills - Staggered Animation */}
              <div className="flex flex-wrap items-center gap-2 md:gap-4 justify-center lg:justify-start text-primary-foreground/80 text-sm">
                {featurePills.map((pill, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-2 px-3 md:px-4 py-2 glass-card rounded-full hover:glass-card-strong transition-all duration-500 hover-lift touch-active ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                    style={{ transitionDelay: `${600 + index * 100}ms` }}
                  >
                    <pill.icon className={`w-4 h-4 ${pill.color}`} />
                    <span className="text-xs md:text-sm whitespace-nowrap">{pill.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Right Content - Hero Image with Parallax */}
          <div 
            className={`relative perspective-1000 transition-all duration-1000 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
            }`}
            style={{ 
              transform: prefersReducedMotion ? undefined : `translateY(${parallaxY}px)`,
            }}
          >
            {/* Glow Effect Behind Image */}
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-pink/30 via-brand-orange/30 to-brand-cyan/30 rounded-3xl blur-[100px] animate-pulse-glow" />
            
            {/* Floating Border Frame */}
            <div className="absolute -inset-4 bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan rounded-3xl opacity-20 blur-lg animate-float" />
            
            {/* Main Image Card */}
            <div className="relative glass-card-strong rounded-2xl p-2 hover-glow">
              <img 
                src={heroImage} 
                alt="Outlook add contact from email signature automatically with Email2Contact AI-powered add-in"
                className="relative rounded-xl shadow-elevated w-full max-w-sm mx-auto md:max-w-none lg:max-w-xl xl:max-w-2xl transform hover:scale-[1.02] transition-transform duration-500"
                loading="eager"
              />
            </div>
            
            {/* Floating Elements - Hidden on mobile for performance */}
            <div className="hidden md:block absolute -top-6 -right-6 w-24 h-24 bg-brand-pink/20 rounded-full blur-2xl animate-blob" />
            <div className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 bg-brand-cyan/20 rounded-full blur-2xl animate-blob animation-delay-2000" />
          </div>
        </div>
      </div>
      
      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background via-background/80 to-transparent" />
    </section>
  );
};
